import React from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { SEOHead } from './components/SEO';
import { PerformanceMonitor } from './components/performance';
import { AccessibilityEnhancer } from './components/accessibility';
import ImagePreloader, { CRITICAL_IMAGES } from './components/common/ImagePreloader';
import DashboardPage from './pages/DashboardPage';
import WaterQualityPage from './pages/WaterQualityPage';
import NutrientDeficiencyPage from './pages/NutrientDeficiencyPage';
import WorkingSolutionPage from './pages/WorkingSolutionPage';
import FormulationPage from './views/FormulationPage';
import FormulationWorkflowPage from './pages/FormulationWorkflowPage';
import SaltLibraryWorkflowPage from './pages/SaltLibraryWorkflowPage';
import StockSolutionsWorkflowPage from './pages/StockSolutionsWorkflowPage';
import UnifiedCalculatorPage from './pages/UnifiedCalculatorPage';
import Results from './views/Results';
import Header from './components/Header';
import Footer from './components/Footer';
import CropRecommendationPage from './pages/CropRecommendationPage';
import NutrientVisualizationPage from './pages/NutrientVisualizationPage';
import ElementAndSaltManagementPage from './pages/ElementAndSaltManagementPage';
import { UIComponentsDemo } from './components/demo';
import ThemeProvider from './styles/ThemeProvider';
import RegisterForm from './components/auth/RegisterForm';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthBootstrap from './components/auth/AuthBootstrap';
import UserProfile from './components/auth/UserProfile';
import LandingPage from './components/landing/LandingPage';
import LogoTest from './components/test/LogoTest';
import ElegantLoginForm from './components/auth/ElegantLoginForm';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import EmailVerification from './components/auth/EmailVerification';
import AdminDashboard from './pages/AdminDashboard';
import SupportPage from './components/support/SupportPage';
import VideoTutorialsPage from './components/support/VideoTutorialsPage';
import AgronomicPrinciplesPage from './components/support/AgronomicPrinciplesPage';
import WaterQualityManagementPage from './components/support/WaterQualityManagementPage';
import AdvancedCalculationsPage from './components/support/AdvancedCalculationsPage';
import YieldOptimizationPage from './components/support/YieldOptimizationPage';

/** DSS shell — no feature banners, no side chrome */
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Header />
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          py: { xs: 1.5, md: 2 },
          width: '100%',
          outline: 'none',
        }}
      >
        {children}
      </Box>
      <Footer />
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthBootstrap>
          {/* Single global SEO owner — public + app routes (noIndex on app) */}
          <SEOHead />
          <PerformanceMonitor enableReporting={true} enableWebVitals={true} />
          <ImagePreloader images={CRITICAL_IMAGES} priority={true} />

          <AccessibilityEnhancer>
            <Routes>
              {/* Public */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<ElegantLoginForm />} />
              <Route path="/register" element={<RegisterForm />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordForm />} />
              <Route path="/verify-email/:token" element={<EmailVerification />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/support/video-tutorials" element={<VideoTutorialsPage />} />
              <Route path="/support/agronomic-principles" element={<AgronomicPrinciplesPage />} />
              <Route path="/support/water-quality-management" element={<WaterQualityManagementPage />} />
              <Route path="/support/advanced-calculations" element={<AdvancedCalculationsPage />} />
              <Route path="/support/yield-optimization" element={<YieldOptimizationPage />} />

              {/* Canonical redirects (DSS route hygiene) */}
              <Route path="/water-profile" element={<Navigate to="/water-quality" replace />} />
              <Route path="/stock-solution" element={<Navigate to="/stock-solutions" replace />} />

              {/* Protected — primary DSS pipeline */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/water-quality"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WaterQualityPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UserProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <Layout>
                      <AdminDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/salt-library"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SaltLibraryWorkflowPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/formulation"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <FormulationWorkflowPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/formulations"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <FormulationPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stock-solutions"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StockSolutionsWorkflowPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              {/* Legacy Stage 5 URL → Results (or Recipe if empty) */}
              <Route
                path="/calculation"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UnifiedCalculatorPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/results"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Results />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Secondary tools — kept, not in primary nav */}
              <Route
                path="/working-solution"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <WorkingSolutionPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nutrient-deficiency"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <NutrientDeficiencyPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crop-recommendation"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CropRecommendationPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nutrient-visualization"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <NutrientVisualizationPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/element-management"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ElementAndSaltManagementPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Secondary / unlinked surfaces (not in DSS primary nav) */}
              <Route
                path="/ui-demo"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UIComponentsDemo />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/logo-test" element={<LogoTest />} />
            </Routes>
          </AccessibilityEnhancer>
        </AuthBootstrap>
      </Router>
    </ThemeProvider>
  );
};

export default App;
